import React, { useState } from 'react';
import { PrimeReactProvider } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tooltip } from 'primereact/tooltip';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

// Datos iniciales
const initialLANGS = {
    ES: {
        updated: "ACTUALIZADO: HOY",
        pst: "PST",
        crossing_pedestrian: "Cruce peatonal",
        crossing_vehicle: "Cruce vehicular",
        wait_time: "Tiempo espera",
        open_lines: "líneas abiertas",
        line_open: "línea abierta",
        no_lines: "No hay líneas abiertas",
        no_wait: "No hay espera",
        minute: "MINUTO",
        minutes: "MINUTOS",
        hour: "HORA",
        hours: "HORAS",
        not_available: "No disponible",
        closed: "CERRADO",
        open: "ABIERTO",
        schedule: "HORARIO",
        faqs_title: "Preguntas Frecuentes",
        faqs_desc: "Encuentra respuestas a las preguntas más comunes sobre los cruces fronterizos.",
        credits: "Créditos",
        by: "por",
        mexico_to_usa: "México →",
        usa_bold: "Estados Unidos",
        updated_today: "ACTUALIZADO: HOY",
        no_answer: "No hay respuesta para la pregunta"
    },
    EN: {
        updated: "UPDATED: TODAY",
        pst: "PST",
        crossing_pedestrian: "Pedestrian crossing",
        crossing_vehicle: "Vehicle crossing",
        wait_time: "Wait time",
        open_lines: "open lanes",
        line_open: "open lane",
        no_lines: "No open lanes",
        no_wait: "No wait",
        minute: "MINUTE",
        minutes: "MINUTES",
        hour: "HOUR",
        hours: "HOURS",
        not_available: "Not available",
        closed: "CLOSED",
        open: "OPEN",
        schedule: "SCHEDULE",
        faqs_title: "FAQs",
        faqs_desc: "Find answers to the most common questions about border crossings.",
        credits: "Credits",
        by: "by",
        mexico_to_usa: "Mexico →",
        usa_bold: "United States",
        updated_today: "UPDATED: TODAY",
        no_answer: "No answer for question"
    },
    FR: {
        updated: "MIS À JOUR: AUJOURD'HUI",
        pst: "PST",
        crossing_pedestrian: "Passage piéton",
        crossing_vehicle: "Passage véhicule",
        wait_time: "Temps d'attente",
        open_lines: "voies ouvertes",
        line_open: "voie ouverte",
        no_lines: "Aucune voie ouverte",
        no_wait: "Aucune attente",
        minute: "MINUTE",
        minutes: "MINUTES",
        hour: "HEURE",
        hours: "HEURES",
        not_available: "Non disponible",
        closed: "FERMÉ",
        open: "OUVERT",
        schedule: "HORAIRE",
        faqs_title: "FAQ",
        faqs_desc: "Trouvez des réponses aux questions les plus courantes sur les passages frontaliers.",
        credits: "Crédits",
        by: "par",
        mexico_to_usa: "Mexique →",
        usa_bold: "États-Unis",
        updated_today: "MIS À JOUR: AUJOURD'HUI",
        no_answer: "Pas de réponse pour la question"
    }
};

const LanguageManager = () => {
    const [langs, setLangs] = useState(initialLANGS);
    const [selectedKey, setSelectedKey] = useState(null);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [newKeyDialogVisible, setNewKeyDialogVisible] = useState(false);
    const [editValue, setEditValue] = useState({ ES: '', EN: '' });
    const [newKey, setNewKey] = useState('');
    const [filter, setFilter] = useState('');
    const [changesMade, setChangesMade] = useState(false);

    // Preparar datos para la tablaa
    const tableData = Object.keys(langs.ES).map(key => ({
        key,
        ES: langs.ES[key],
        EN: langs.EN[key],
        FR: langs.FR[key]
    }));

    // Filtrar datos
    const filteredData = tableData.filter(item =>
        item.key.toLowerCase().includes(filter.toLowerCase()) ||
        item.ES.toLowerCase().includes(filter.toLowerCase()) ||
        item.EN.toLowerCase().includes(filter.toLowerCase()) ||
        item.FR.toLowerCase().includes(filter.toLowerCase())
    );

    // Abrir diálogo de edición
    const openEditDialog = (key) => {
        setSelectedKey(key);
        setEditValue({
            ES: langs.ES[key] || '',
            EN: langs.EN[key] || '',
            FR: langs.FR[key] || ''
        });
        setEditDialogVisible(true);
    };

    // Guardar cambios de edición
    const saveEdit = () => {
        if (selectedKey) {
            const updatedLangs = { ...langs };
            updatedLangs.ES[selectedKey] = editValue.ES;
            updatedLangs.EN[selectedKey] = editValue.EN;
            setLangs(updatedLangs);
            setChangesMade(true);
        }
        setEditDialogVisible(false);
    };

    // Añadir nueva clave
    const addNewKey = () => {
        if (newKey && !langs.ES[newKey]) {
            const updatedLangs = { ...langs };
            updatedLangs.ES[newKey] = '';
            updatedLangs.EN[newKey] = '';
            setLangs(updatedLangs);
            setNewKey('');
            setChangesMade(true);
        }
        setNewKeyDialogVisible(false);
    };

    // Eliminar clave
    const confirmDelete = (key) => {
        confirmDialog({
            message: `Are you sure you want to delete the key "${key}"? This action cannot be undone.`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            accept: () => deleteKey(key),
            acceptClassName: 'p-button-danger'
        });
    };

    const deleteKey = (key) => {
        const updatedLangs = { ...langs };
        delete updatedLangs.ES[key];
        delete updatedLangs.EN[key];
        setLangs(updatedLangs);
        setChangesMade(true);
    };

    // Restablecer cambios
    const resetChanges = () => {
        confirmDialog({
            message: '¿Estás seguro de que quieres descartar todos los cambios?',
            header: 'Confirmar restablecimiento',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                setLangs(initialLANGS);
                setChangesMade(false);
            },
            acceptClassName: 'p-button-warning'
        });
    };

    // Exportar traducciones
    const exportTranslations = () => {
        const dataStr = JSON.stringify(langs, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

        const exportFileDefaultName = 'translations.json';

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    };

    // Barra de herramientas
    const leftToolbarTemplate = () => (
        <div className="flex flex-wrap gap-2">
            <Button
                label="Nueva clave"
                icon="pi pi-plus"
                onClick={() => setNewKeyDialogVisible(true)}
            />
            <Button
                label="Export"
                icon="pi pi-download"
                onClick={exportTranslations}
                className="p-button-help"
            />
            {changesMade && (
                <Button
                    label="Restablecer"
                    icon="pi pi-refresh"
                    onClick={resetChanges}
                    className="p-button-warning"
                />
            )}
        </div>
    );

    const rightToolbarTemplate = () => (
        <span className="p-input-icon-left">
            {/* <i className="pi pi-search" /> */}
            <InputText
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Buscar..."
            />
        </span>
    );

    // Plantilla de acciones para la tabla
    const actionsTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button
                icon="pi pi-pencil"
                rounded
                text
                severity="secondary"
                onClick={() => openEditDialog(rowData.key)}
                tooltip="Edit"
                tooltipOptions={{ position: 'top' }}
            />
            <Button
                icon="pi pi-trash"
                rounded
                text
                severity="danger"
                onClick={() => confirmDelete(rowData.key)}
                tooltip="Delete"
                tooltipOptions={{ position: 'top' }}
            />
        </div>
    );

    return (
        <PrimeReactProvider>
            <div className="container mx-auto">
                <ConfirmDialog />
                <Tooltip target=".tooltip-target" />


                <div>

                    <div className="mb-4">
                        <h1 className="text-3xl font-bold text-gray-800">Language Manager</h1>
                    </div>

                    <Card>
                        {
                            false && <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="mb-4" />
                        }

                        <div className="card">
                            <DataTable
                                value={filteredData}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} entradas"
                                emptyMessage="No se encontraron traducciones."
                                scrollable
                                scrollHeight="flex"
                                resizableColumns
                                columnResizeMode="expand"
                                showGridlines
                                size="small"
                                stripedRows
                            >
                                <Column field="key" header="Key" sortable style={{ minWidth: '200px' }} frozen />
                                <Column field="ES" header="Spanish" sortable style={{ minWidth: '250px' }} />
                                <Column field="EN" header="English" sortable style={{ minWidth: '250px' }} />
                                <Column field="FR" header="French" sortable style={{ minWidth: '250px' }} />
                                <Column body={actionsTemplate} header="Actions" style={{ width: '120px' }} />
                            </DataTable>
                        </div>
                    </Card>

                    {/* Diálogo de edición */}
                    {/* Diálogo de edición - Versión mejorada */}
                    <Dialog
                        header={`Edit translation: ${selectedKey}`}
                        visible={editDialogVisible}
                        style={{ width: '60vw' }}
                        onHide={() => {
                            if (JSON.stringify(editValue) !== JSON.stringify({
                                ES: langs.ES[selectedKey] || '',
                                EN: langs.EN[selectedKey] || '',
                                FR: langs.FR[selectedKey] || ''
                            })) {
                                confirmDialog({
                                    message: 'There are unsaved changes. Are you sure you want to cancel?',
                                    header: 'Confirm',
                                    icon: 'pi pi-exclamation-triangle',
                                    accept: () => setEditDialogVisible(false),
                                    reject: () => { }
                                });
                            } else {
                                setEditDialogVisible(false);
                            }
                        }}
                        footer={
                            <div className="flex justify-between w-full">
                                <div>
                                    <Button
                                        label="Cancelar"
                                        icon="pi pi-times"
                                        text
                                        onClick={() => {
                                            if (JSON.stringify(editValue) !== JSON.stringify({
                                                ES: langs.ES[selectedKey] || '',
                                                EN: langs.EN[selectedKey] || '',
                                                FR: langs.FR[selectedKey] || ''
                                            })) {
                                                confirmDialog({
                                                    message: 'There are unsaved changes. Are you sure you want to cancel?',
                                                    header: 'Confirmar',
                                                    icon: 'pi pi-exclamation-triangle',
                                                    accept: () => setEditDialogVisible(false),
                                                    reject: () => { }
                                                });
                                            } else {
                                                setEditDialogVisible(false);
                                            }
                                        }}
                                    />
                                </div>
                                <div>
                                    <Button
                                        label="Restaurar original"
                                        icon="pi pi-refresh"
                                        className="p-button-secondary mr-2"
                                        onClick={() => {
                                            setEditValue({
                                                ES: initialLANGS.ES[selectedKey] || '',
                                                EN: initialLANGS.EN[selectedKey] || ''
                                            });
                                        }}
                                    />
                                    <Button
                                        label="Guardar"
                                        icon="pi pi-check"
                                        onClick={saveEdit}
                                        autoFocus
                                        disabled={
                                            editValue.ES.trim() === '' ||
                                            editValue.EN.trim() === '' ||
                                            editValue.FR.trim() === '' ||
                                            (editValue.ES === langs.ES[selectedKey] &&
                                                editValue.EN === langs.EN[selectedKey] &&
                                                editValue.FR === langs.FR[selectedKey])
                                        }
                                    />
                                </div>
                            </div>
                        }
                    >
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2 text-gray-700">Key:</label>
                            <InputText value={selectedKey} disabled className="w-full p-2 bg-gray-100 rounded" />
                            <small className="mt-2 text-gray-500">This key <strong>is used in the code</strong> to reference the translation.</small>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded p-4 bg-blue-50 border-blue-200">
                                <h3 className="text-lg font-semibold mb-3 text-blue-800 flex items-center">
                                    <i className="pi pi-language mr-2"></i>
                                    Español (ES)
                                </h3>
                                <InputText
                                    value={editValue.ES}
                                    onChange={(e) => setEditValue({ ...editValue, ES: e.target.value })}
                                    className="w-full mb-2"
                                    autoFocus
                                />
                                <div className="text-xs text-gray-500 mt-1">
                                    Characters: {editValue.ES?.length}
                                    {initialLANGS.ES[selectedKey] && editValue.ES !== initialLANGS.ES[selectedKey] && (
                                        <span className="ml-2 text-orange-600">
                                            <i className="pi pi-exclamation-triangle mr-1"></i>
                                            Modified
                                        </span>
                                    )}
                                </div>
                                {initialLANGS.ES[selectedKey] && editValue.ES !== initialLANGS.ES[selectedKey] && (
                                    <div className="mt-2 p-2 bg-yellow-100 text-yellow-800 rounded text-xs">
                                        <div className="font-semibold">Original value:</div>
                                        <div>{initialLANGS.ES[selectedKey]}</div>
                                    </div>
                                )}
                            </div>

                            <div className="border rounded p-4 bg-green-50 border-green-200">
                                <h3 className="text-lg font-semibold mb-3 text-green-800 flex items-center">
                                    <i className="pi pi-language mr-2"></i>
                                    English (EN)
                                </h3>
                                <InputText
                                    value={editValue.EN}
                                    onChange={(e) => setEditValue({ ...editValue, EN: e.target.value })}
                                    className="w-full mb-2"
                                />
                                <div className="text-xs text-gray-500 mt-1">
                                    Characters: {editValue.EN?.length}
                                    {initialLANGS.EN[selectedKey] && editValue.EN !== initialLANGS.EN[selectedKey] && (
                                        <span className="ml-2 text-orange-600">
                                            <i className="pi pi-exclamation-triangle mr-1"></i>
                                            Modified
                                        </span>
                                    )}
                                </div>
                                {initialLANGS.EN[selectedKey] && editValue.EN !== initialLANGS.EN[selectedKey] && (
                                    <div className="mt-2 p-2 bg-yellow-100 text-yellow-800 rounded text-xs">
                                        <div className="font-semibold">Original value:</div>
                                        <div>{initialLANGS.EN[selectedKey]}</div>
                                    </div>
                                )}
                            </div>

                            <div className="border rounded p-4 bg-purple-50 border-purple-200">
                                <h3 className="text-lg font-semibold mb-3 text-purple-800 flex items-center">
                                    <i className="pi pi-language mr-2"></i>
                                    Français (FR)
                                </h3>
                                <InputText
                                    value={editValue.FR}
                                    onChange={(e) => setEditValue({ ...editValue, FR: e.target.value })}
                                    className="w-full mb-2"
                                />
                                <div className="text-xs text-gray-500 mt-1">
                                    Characters: {editValue.FR?.length}
                                    {initialLANGS.FR[selectedKey] && editValue.FR !== initialLANGS.FR[selectedKey] && (
                                        <span className="ml-2 text-orange-600">
                                            <i className="pi pi-exclamation-triangle mr-1"></i>
                                            Modified
                                        </span>
                                    )}
                                </div>
                                {initialLANGS.FR[selectedKey] && editValue.FR !== initialLANGS.FR[selectedKey] && (
                                    <div className="mt-2 p-2 bg-yellow-100 text-yellow-800 rounded text-xs">
                                        <div className="font-semibold">Original value:</div>
                                        <div>{initialLANGS.FR[selectedKey]}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-4 p-3 bg-gray-100 rounded">
                            <h4 className="font-medium mb-2">Previous view:</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="p-2 bg-white border rounded">
                                    <span className="text-sm text-gray-500">ES:</span>
                                    <span className="ml-1">{editValue.ES || <span className="text-gray-400">(vacío)</span>}</span>
                                </div>
                                <div className="p-2 bg-white border rounded">
                                    <span className="text-sm text-gray-500">EN:</span>
                                    <span className="ml-1">{editValue.EN || <span className="text-gray-400">(vacío)</span>}</span>
                                </div>
                                <div className="p-2 bg-white border rounded">
                                    <span className="text-sm text-gray-500">FR:</span>
                                    <span className="ml-1">{editValue.FR || <span className="text-gray-400">(vacío)</span>}</span>
                                </div>
                            </div>
                        </div>
                    </Dialog>
                    {/* Diálogo para nueva clave */}
                    <Dialog
                        header="New translation key"
                        visible={newKeyDialogVisible}
                        style={{ width: '30vw' }}
                        onHide={() => setNewKeyDialogVisible(false)}
                        footer={
                            <div>
                                <Button label="Cancel" icon="pi pi-times" text onClick={() => setNewKeyDialogVisible(false)} />
                                <Button label="Create" icon="pi pi-check" onClick={addNewKey} autoFocus />
                            </div>
                        }
                    >
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Key name:</label>
                            <InputText
                                value={newKey}
                                onChange={(e) => setNewKey(e.target.value)}
                                className="w-full"
                                autoFocus
                                placeholder="Ej: welcome_message"
                            />
                            <small className="text-gray-500">
                                The key should be unique and use underscores (_) instead of spaces.
                            </small>
                        </div>
                    </Dialog>

                    {/* {changesMade && (
                    <Message
                        severity="info"
                        text="Tienes cambios sin guardar. Recuerda exportar las traducciones cuando termines."
                        className="mt-4"
                    />
                )} */}
                </div>
            </div>
        </PrimeReactProvider>
    );
};

export default LanguageManager;